import React from 'react';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import CTAs from '../block-ui/CTAs';
import RichText from '../block-ui/RichText';
import BlockWrapper from '../BlockWrapper';
import type {
  ComposerBlockElement,
  FeatureItem,
  FeatureListBlockProps,
  FeatureListColumn,
} from '../../../types/composer';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import AddToPlaylistIcon from '../../icons/AddToPlaylistIcon';
import { ctaSchema, ctasSchema } from '../../../lib/composer/schemas/cta';
import columnsSchema, {
  featureIconMap,
} from '../../../lib/composer/schemas/featureList';
import themeSchema from '../../../lib/composer/schemas/theme';
import textSchema from '../../../lib/composer/schemas/text';
import {
  ComposerBlocksContextType,
  useComposerBlocksContext,
} from '../BlockContext';

type FeatureItemProps = {
  theme: string;
  feature: FeatureItem;
};

const FeatureItem = (props: FeatureItemProps) => {
  const { feature, theme } = props;
  const textBlue500 = '#3b82f6';

  return (
    <Flex>
      <Flex
        justifyContent="center"
        alignItems="start"
        minWidth="50px"
        mr="0.75em"
        pt={1}
      >
        {feature.icon.name === 'playlist' ? (
          <AddToPlaylistIcon fill={textBlue500} width="2em" height="2em" />
        ) : (
          <Icon
            data-testid="feature-column-item-icon"
            // FIXME: any
            icon={(featureIconMap as any)[feature.icon.name]}
            size="lg"
            color={textBlue500}
          />
        )}
      </Flex>

      <RichText html={feature.title} theme={theme} />
    </Flex>
  );
};

type FeatureColumnProps = {
  theme: string;
  column: FeatureListColumn;
  faded?: boolean;
};

const FeatureColumn = (props: FeatureColumnProps) => {
  const { column, theme, faded } = props;
  return (
    <Flex
      p={2}
      my={4}
      flexDirection="column"
      justifyContent="center"
      className="w-full md:w-1/3 md:max-w-[300px] lg:max-w-[360px]"
      data-testid="feature-column"
      sx={{
        gap: 3,
        opacity: faded ? 0.4 : 1,
        pointerEvents: faded ? 'none' : 'auto',
        transition: 'opacity 0.3s',
      }}
    >
      <Flex flexDirection="column" alignItems={'center'} className="w-full">
        <H3 className="mt-0 text-xl font-semibold text-blue-600 text-center">
          {column.title}
        </H3>
        {column.subtitle && <RichText html={column.subtitle} theme={theme} />}
      </Flex>
      {column.features.map((feature, i) => {
        const iconKey = feature.icon?.name || '';
        const indexKey = String(i);

        return (
          <FeatureItem
            data-testid="feature-item"
            key={`feature-item-${iconKey}-${indexKey}`}
            theme={theme}
            feature={feature}
          />
        );
      })}
    </Flex>
  );
};

type FeatureListProps = FeatureListBlockProps;

/**
 * FeatureList block.
 */
const FeatureList: ComposerBlockElement = (props: FeatureListProps) => {
  const {
    heading,
    subheading,
    columns,
    text,
    theme = '',
    cta: ctaProps,
    ctas: ctasProps,
    anchor,
    topHeader,
  } = props;

  const colors = useColors();
  const context =
    useComposerBlocksContext() || ({} as ComposerBlocksContextType);
  const { disabledColumns = false } = context;

  const ctas = [...(ctaProps?.caption ? [ctaProps] : []), ...(ctasProps || [])];
  const flatColumns = columns?.flat() || [];

  return (
    <Flex bg={colors[theme] || 'bg-0'} id={anchor}>
      <BlockWrapper>
        <Flex flexDirection="column" sx={{ gap: 3 }} alignItems="center">
          {heading && (
            <Heading
              text={heading}
              theme={theme}
              topHeader={topHeader}
              textProps="text-center"
            />
          )}
          {subheading && (
            <Subheading
              text={subheading}
              theme={theme}
              topHeader={topHeader}
              textProps="text-center"
            />
          )}
          <Flex
            alignItems="flex-start"
            justifyContent="center"
            className="flex-col md:flex-row md:space-x-4 gap-8 md:gap-0"
          >
            {flatColumns.map((column, j) => {
              // Create a unique key based on column title and position
              const titleKey = column.title
                ? column.title.replace(/\s+/g, '-').toLowerCase()
                : '';
              const uniqueKey = `feature-column-${titleKey}-${j}`;

              return (
                <FeatureColumn
                  key={uniqueKey}
                  theme={theme}
                  column={column}
                  faded={disabledColumns && j === 2}
                />
              );
            })}
          </Flex>
          <Flex flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
            {text && <RichText html={text} theme={theme} />}
            <CTAs ctas={ctas} theme={theme} />
          </Flex>
        </Flex>
      </BlockWrapper>
    </Flex>
  );
};

FeatureList.schema = {
  name: 'feature-list',
  label: 'Feature List',
  editableProps: [
    headingSchema,
    subheadingSchema,
    columnsSchema,
    textSchema,
    ctaSchema,
    ctasSchema,
    themeSchema,
  ],
};

export default FeatureList;
